// src/contexts/AuthContext.js

import React, { createContext, useState, useEffect, useContext } from 'react';
import { login as loginService, register as registerService, getCurrentUser, logoutUser as logoutService } from '../services/authService';
import { setToken, getToken, removeToken } from '../utils/storage';
import axiosInstance from "../axiosInstance";
import { toast } from 'react-toastify';

export const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isAuthenticated = !!user;

  // Load the token and user on app start
  useEffect(() => {
    let isMounted = true;

    const fetchUser = async () => {
      const token = getToken();
      if (token) {
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        try {
          const data = await getCurrentUser();
          if (isMounted) {
            setUser(data.user);
          }
        } catch (err) {
          console.error("Error fetching user:", err.message);
          removeToken();
          if (isMounted) setUser(null);
        }
      }
      if (isMounted) setLoading(false);
    };

    fetchUser();

    return () => {
      isMounted = false;
    };
  }, []);

  const login = async ({ email, password }) => {
    try {
      const data = await loginService({ email, password });
      setToken(data.token);
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
      setUser(data.user);
      toast.success(data.message || "Logged in successfully!");
    } catch (err) {
      setError(err.response?.data?.message || "Login failed. Please try again.");
      toast.error(err.response?.data?.message || "Login failed. Please try again.");
      throw err;
    }
  };

  const register = async ({ name, email, password }) => {
    try {
      const data = await registerService({ name, email, password });
      setToken(data.token);
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
      setUser(data.user);
      toast.success(data.message || "Registered and logged in successfully!");
    } catch (err) {
      setError(err.response?.data?.message || "Registration failed. Please try again.");
      toast.error(err.response?.data?.message || "Registration failed. Please try again.");
      throw err;
    }
  };

  const logout = async () => {
    try {
      await logoutService();
    } finally {
      removeToken();
      delete axiosInstance.defaults.headers.common['Authorization'];
      setUser(null);
      toast.success("Logged out successfully!");
    }
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, login, register, logout, loading, error }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export default AuthProvider;
